/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { countBy, isEmpty } from 'lodash'

import type {
  RoleAssignments,
  RoleMapping,
  RoleMappingGroupRule,
} from '@modules/cloud-api/v1/types'
import { isOrgOwnerRoleAssignments, isRoleAssignmentsEmpty } from '@modules/role-assignments-lib'

import { formMessages } from './messages'

import type { FormikErrors } from 'formik'
import type { OrgOwnerMappingError, FormError, RuleError } from './types'
import type { IntlShape } from 'react-intl'

// This must be kept in sync with the regex in Scala OrganizationRoleMappingController
const ruleValidationRegex = /^[a-zA-Z0-9_\-*. ]+$/

export const validateForm =
  ({
    formatMessage,
    originalRoleMapping,
    roleMappings = [],
    organizationId,
  }: {
    formatMessage: IntlShape['formatMessage']
    originalRoleMapping?: RoleMapping | undefined
    roleMappings: RoleMapping[] | undefined
    organizationId: string
  }): ((values: RoleMapping) => FormError) =>
  (values) => {
    const errors: FormError = {}

    const orgOwnerError = validateOrgOwner({
      roleMappings,
      originalRoleMapping,
      newRoleAssignments: values.role_assignments,
      newEnabledState: values.enabled,
      organizationId,
    })
    orgOwnerError && (errors.missingOrgOwnerMapping = orgOwnerError)

    const nameError = validateName({
      roleMappings,
      originalRoleMappingName: originalRoleMapping?.name,
      name: values.name,
      formatMessage,
    })
    nameError && (errors.name = nameError)

    const roleAssignmentError = validateRoleAssignments({
      roleAssignments: values.role_assignments,
      formatMessage,
    })
    roleAssignmentError && (errors.role_assignments = roleAssignmentError)

    const ruleMappingType = values.rule.all ? 'all' : 'any'
    const mappingRuleArray = values.rule.all || values.rule.any

    const ruleError = validateRule({
      mappingRuleArray,
      ruleMappingType,
      formatMessage,
    })
    ruleError && (errors.rule = ruleError)

    return isEmpty(errors) ? {} : errors
  }

export const validateRoleAssignments = ({
  roleAssignments,
  formatMessage,
}: {
  roleAssignments: RoleAssignments | undefined
  formatMessage: IntlShape['formatMessage']
}): string | undefined => {
  if (isRoleAssignmentsEmpty(roleAssignments)) {
    return formatMessage(formMessages.roleAssignmentsRequired)
  }

  return undefined
}

export const validateOrgOwner = ({
  roleMappings,
  originalRoleMapping,
  newRoleAssignments,
  newEnabledState,
  organizationId,
}: {
  roleMappings: RoleMapping[]
  originalRoleMapping?: RoleMapping | undefined
  newRoleAssignments: RoleAssignments | undefined
  newEnabledState: boolean
  organizationId: string
}): OrgOwnerMappingError | undefined => {
  const isOrgOwnerRole = (roleAssignments: RoleAssignments | undefined) =>
    isOrgOwnerRoleAssignments({
      roleAssignments,
      organizationId,
    })

  const isEnabledOrgOwnerRole = (roleAssignments: RoleAssignments | undefined, enabled: boolean) =>
    isOrgOwnerRole(roleAssignments) && enabled

  if (roleMappings.length === 0 && !isEnabledOrgOwnerRole(newRoleAssignments, newEnabledState)) {
    return 'FIRST_MAPPING_MUST_BE_ORG_OWNER'
  }

  if (roleMappings.length === 0) {
    return
  }

  const orgOwnerRoleMappings = roleMappings.filter((roleMapping) =>
    isEnabledOrgOwnerRole(roleMapping.role_assignments, roleMapping.enabled),
  )
  const isTheLastOrgOwnerRole =
    roleMappings.length >= 1 &&
    orgOwnerRoleMappings.length === 1 &&
    orgOwnerRoleMappings[0]?.name === originalRoleMapping?.name

  if (isTheLastOrgOwnerRole && !isOrgOwnerRole(newRoleAssignments)) {
    return 'CANNOT_REMOVE_THE_ONLY_ORG_OWNER_MAPPING'
  }

  if (isTheLastOrgOwnerRole && !newEnabledState) {
    return 'CANNOT_DISABLE_ORG_OWNER_LAST_MAPPING'
  }

  return undefined
}

export const validateName = ({
  roleMappings,
  originalRoleMappingName,
  name,
  formatMessage,
}: {
  roleMappings: RoleMapping[]
  originalRoleMappingName?: string
  name: string
  formatMessage: IntlShape['formatMessage']
}): string | undefined => {
  if (!name) {
    return formatMessage(formMessages.nameRequired)
  }

  const existingRoleMapping = roleMappings.find((roleMapping) => roleMapping.name === name)

  if (existingRoleMapping && existingRoleMapping.name !== originalRoleMappingName) {
    return formatMessage(formMessages.roleMappingNameExists)
  }

  return undefined
}

export const validateRule = ({
  mappingRuleArray,
  ruleMappingType,
  formatMessage,
}: {
  mappingRuleArray: RoleMappingGroupRule[] | undefined
  ruleMappingType: string
  formatMessage: IntlShape['formatMessage']
}): RuleError | undefined => {
  if (!ruleMappingType || !mappingRuleArray || mappingRuleArray.length === 0) {
    return formatMessage(formMessages.ruleRequired)
  }

  if (mappingRuleArray.length > 0) {
    const innerRuleError = validateMappingRules(mappingRuleArray, ruleMappingType, formatMessage)

    if (innerRuleError) {
      return innerRuleError
    }
  }

  return undefined
}

function validateMappingRules(
  mappingRuleArray: RoleMappingGroupRule[],
  ruleMappingType: string,
  formatMessage,
): RuleError | undefined {
  const ruleErrors: RuleError = {}

  const groupNames = mappingRuleArray.map((el) => el.group)
  const groupCount = countBy(groupNames)
  ruleErrors[`${ruleMappingType}`] = ruleErrors[`${ruleMappingType}`] || []
  const ruleTypeErrors: string[] = ruleErrors[`${ruleMappingType}`] as string[]

  mappingRuleArray.forEach((attribute, index) => {
    const groupValue = attribute.group

    if (!groupValue) {
      ruleTypeErrors[index] = formatMessage(formMessages.attributeValueRequired)
      return
    }

    if (!groupValue.match(ruleValidationRegex)) {
      ruleTypeErrors[index] = formatMessage(formMessages.ruleSyntax)
      return
    }

    if ((groupCount[groupValue] ?? 0) > 1) {
      ruleTypeErrors[index] = formatMessage(formMessages.duplicateGroupValue)
    }
  })

  return isEmpty(ruleErrors) || (isEmpty(ruleErrors.any) && isEmpty(ruleErrors.all))
    ? undefined
    : ruleErrors
}

export const hasFormErrors = (errors: FormikErrors<any>): boolean =>
  Object.values(errors).some((error) => !!error)

export const checkHasToRunVerification = ({
  roleMappings,
  originalRoleMapping,
  organizationId,
}: {
  roleMappings: RoleMapping[] | undefined
  originalRoleMapping: RoleMapping | undefined
  organizationId: string
}): boolean =>
  !roleMappings?.length || // No existing role mappings, creating the first mapping
  (originalRoleMapping?.enabled === true && // Original role mapping is enabled
    isOrgOwnerRoleAssignments({
      // Original role mapping is org owner
      roleAssignments: originalRoleMapping?.role_assignments,
      organizationId,
    }))
